import { PricesAsset } from "@main-services/prices-scroll.service";
import { Tradable } from "@models/interfaces/tradable";
import { Dictionary } from "@helpers/types";
import { Fiat } from "@models";
import { toCurrencyByFiatId } from "@helpers/tradable-convert";
import { Index } from "@models/asset/index/index.model";
import { getTradableChange } from "@modules/trade/helpers/helper";
import { MIN_PERCENTAGE_DISPLAY, TRADABLES_TYPE } from "@helpers/constants";
import { Tradable as ITradable } from "@modules/+new/core/tradables/domain/entities/tradable";
import { Timespan } from "@helpers/const/timespan";
import { PriceChange } from "@modules/+new/core/tradables/domain/entities/price-change";

////// Prices 2.0 CALC METHODS //////

export function getPrice(pricesAsset: PricesAsset): number {
	return pricesAsset.price_data.price;
}

// remaps properties to match Timespan enum
export function getChangePercent(priceChange: PriceChange): Dictionary<number> {
	return {
		[Timespan.Day]: priceChange.day,
		[Timespan.Week]: priceChange.week,
		[Timespan.Month]: priceChange.month,
		[Timespan.Year]: priceChange.year,
		[Timespan.SixMonths]: priceChange.six_months,
		[Timespan.FiveYears]: priceChange.five_years
	};
}

export function getMarketCap(mcap: number, isIndex: boolean, fiat: Fiat): number {
	return isIndex ? mcap : toCurrencyByFiatId(mcap, fiat);
}

////// CALC METHODS //////

export function getMarketCapOld(tradable: Tradable, fiat: Fiat): number {
	return getFiatAmount(tradable, fiat) * tradable.getCirculatingSupply();
}

export function getFiatAmount(tradable: Tradable, fiat: Fiat): number {
	return isIndex(tradable) ? tradable.getPrice() : toCurrencyByFiatId(tradable.getPrice(), fiat);
}

export function isIndex(tradable: Tradable): boolean {
	return tradable instanceof Index;
}

export function getChartColorClasses(tradable: Tradable): Dictionary<string> {
	return {
		[Timespan.Day]: getChartColorClass(tradable, Timespan.Day),
		[Timespan.Week]: getChartColorClass(tradable, Timespan.Week),
		[Timespan.Month]: getChartColorClass(tradable, Timespan.Month),
		[Timespan.Year]: getChartColorClass(tradable, Timespan.Year),
		[Timespan.SixMonths]: getChartColorClass(tradable, Timespan.SixMonths),
		[Timespan.FiveYears]: getChartColorClass(tradable, Timespan.FiveYears)
	};
}

export function getChartColorClassesNew(pricesAsset: PricesAsset): Dictionary<string> {
	return {
		[Timespan.Day]: getChartColorClassNew(pricesAsset, Timespan.Day),
		[Timespan.Week]: getChartColorClassNew(pricesAsset, Timespan.Week),
		[Timespan.Month]: getChartColorClassNew(pricesAsset, Timespan.Month),
		[Timespan.Year]: getChartColorClassNew(pricesAsset, Timespan.Year),
		[Timespan.SixMonths]: getChartColorClassNew(pricesAsset, Timespan.SixMonths),
		[Timespan.FiveYears]: getChartColorClassNew(pricesAsset, Timespan.FiveYears)
	};
}

export function getChartColorClassNew(pricesAsset: PricesAsset, timeframe: Timespan): string {
	const change = getChangePercent(pricesAsset.price_data.change_percent)[timeframe];

	if (change < 0) {
		return "decreaseColor";
	} else if (change > 0) {
		return "increaseColor";
	}

	return "noChangeColor";
}

export function getChartColorClass(
	tradable: Tradable,
	timeframe: Timespan
): "noColorCoding" | "increaseColor" | "decreaseColor" | "noChangeColor" {
	return isChangeNegative(tradable, timeframe)
		? "decreaseColor"
		: isChangePositive(tradable, timeframe)
			? "increaseColor"
			: "noChangeColor";
}

export function isChangePositive(tradable: Tradable, timeframe: Timespan): boolean {
	return +getTradableChange(tradable, timeframe).changePercent >= MIN_PERCENTAGE_DISPLAY;
}

export function isChangeNegative(tradable: Tradable, timeframe: Timespan): boolean {
	return +getTradableChange(tradable, timeframe).changePercent <= -MIN_PERCENTAGE_DISPLAY;
}

// "NEW TRADABLES" CALC METHODS

export function getMarketCapNew(tradable: ITradable, fiat: Fiat): number {
	return getFiatAmountNew(tradable, fiat) * tradable.assetPrice.circulatingSupply;
}

export function getFiatAmountNew(tradable: ITradable, fiat: Fiat): number {
	return isIndexNew(tradable) ? tradable.assetPrice.price : toCurrencyByFiatId(tradable.assetPrice.price, fiat);
}

export function isIndexNew(tradable: ITradable): boolean {
	return tradable.type === TRADABLES_TYPE.INDEX;
}
